import { useEffect, useState, useDeferredValue } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Divider from '@mui/material/Divider'
import MuiButton from '@mui/material/Button'
import { Link as RouterLink } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import FilterListIcon from '@mui/icons-material/FilterList'
import PageBanner from '../components/common/PageBanner'
import Container from '../components/common/Container'
import FarmerGroupImage from '../components/common/FarmerGroupImage'
import TagPill from '../components/common/TagPill'
import OrganisationCard from '../components/directory/OrganisationCard'
import type { Organisation } from '../components/directory/OrganisationCard'
import EmptyState from '../components/common/EmptyState'
import PageLoader from '../components/common/PageLoader'
import { API_BASE_URL } from '../config/env'
import { useToast } from '../context/ToastContext'
import { reportApiError } from '../lib/reportApiError'

const INITIAL_SHOW = 6

export default function Directory() {
  const { showToast } = useToast()
  const [orgData, setOrgData] = useState<Organisation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [fetchKey, setFetchKey] = useState(0)
  const [search, setSearch] = useState('')
  const [searchLimit, setSearchLimit] = useState(12)
  const [activeType, setActiveType] = useState<string | null>(null)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())

  useEffect(() => {
    document.title = 'Ecosystem Directory — Future of Farm Food Library'
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(false)
    fetch(`${API_BASE_URL}/directory`)
      .then((res) => {
        if (!res.ok) {
          const err = new Error(`Failed to load directory (${res.status})`) as Error & { status?: number }
          err.status = res.status
          throw err
        }
        return res.json() as Promise<Organisation[]>
      })
      .then((data) => { setOrgData(data); setLoading(false) })
      .catch((err: Error & { status?: number }) => {
        reportApiError(err, { endpoint: '/directory', status: err.status })
        setError(true)
        setLoading(false)
        showToast('Could not load the directory. Please try again.', 'error')
      })
  }, [fetchKey, showToast])

  const TYPES = [...new Set(orgData.flatMap((o) => o.stakeholderType).filter(Boolean))].sort()

  const deferredSearch = useDeferredValue(search)
  const isSearching = search.trim().length >= 2

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setSearchLimit(12)
    if (value.trim().length >= 2) setActiveType(null)
  }

  const searchResults = isSearching
    ? orgData.filter(
      (org) => {
        const q = deferredSearch.toLowerCase()
        return (
          org.name.toLowerCase().includes(q) ||
          org.description.toLowerCase().includes(q) ||
          org.stakeholderType.some((t) => t.toLowerCase().includes(q))
        )
      }
    )
    : []

  const visibleTypes = activeType ? [activeType] : []
  const groups = visibleTypes.map((type) => ({
    type,
    orgs: orgData.filter((o) => o.stakeholderType.includes(type)),
  }))

  const toggleExpand = (type: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  return (
    <>
      <PageBanner
        overline="Ecosystem Directory"
        headline="Help Build the Regenerative Agriculture Ecosystem"
        subtext="Mapping the organisations, institutions, and networks contributing to regenerative agriculture in South Africa."
      />

      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'background.default' }}>
        <Container>

          {/* Intro copy + image */}
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ mb: 5 }}>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', justifyContent: 'center' }}>
              <FarmerGroupImage />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
                South Africa’s regenerative agriculture ecosystem is still emerging. While there is growing interest from farmers, food processors and retailers, investors, consumers, policy makers, researchers, and support organisations, many initiatives remain fragmented and disconnected.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2.5 }}>
                This directory is not an exhaustive map of the ecosystem. It is a growing, community-driven resource designed to help make regenerative agriculture activity more visible, searchable and connected over time.
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Discover and explore organisations by stakeholder type, including aggregators, service providers, agricultural funders, regenerative and organic farmers, technical assistance providers, researchers, and certification systems.
              </Typography>
            </Grid>
          </Grid>

          {loading && <PageLoader message="Loading directory…" />}
          {error && (
            <EmptyState
              heading="Could not load the directory"
              body="Please check your connection and try again."
              action={{ label: 'Try again', onClick: () => setFetchKey((k) => k + 1) }}
            />
          )}

          {!loading && !error && (
            <>
              {/* Search bar */}
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
                <Box sx={{ px: 3, py: 2, backgroundColor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SearchIcon sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Search by name, type, or keyword
                  </Typography>
                </Box>
                <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default' }}>
                  <TextField
                    fullWidth
                    placeholder="Search organisations (name, type, keyword)…"
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    variant="outlined"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      },
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                  />
                </Box>
              </Paper>

              {/* Search results */}
              {isSearching && (
                <>
                  {searchResults.length > 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                      {searchResults.length} organisation{searchResults.length !== 1 ? 's' : ''} matching &ldquo;{search}&rdquo;
                    </Typography>
                  )}
                  {searchResults.length === 0 ? (
                    <EmptyState
                      heading="No organisations found"
                      body="Try a different name or stakeholder type, or browse all groups below."
                      action={{ label: 'Browse all types', onClick: () => setSearch('') }}
                    />
                  ) : (
                    <>
                      <Grid container spacing={3}>
                        {searchResults.slice(0, searchLimit).map((org) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={org.id}>
                            <OrganisationCard org={org} />
                          </Grid>
                        ))}
                      </Grid>
                      {searchResults.length > searchLimit && (
                        <Box sx={{ mt: 4, textAlign: 'center' }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={() => setSearchLimit((prev) => prev + 12)}
                            endIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                          >
                            Load more ({searchResults.length - searchLimit} remaining)
                          </Button>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {/* OR + filter by type */}
              {!isSearching && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 4 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography sx={{ color: 'secondary.main', fontWeight: 800, fontSize: '1.1rem', letterSpacing: '0.12em' }}>
                      OR
                    </Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', mb: 4 }}>
                    <Box sx={{ px: 3, py: 2, backgroundColor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <FilterListIcon sx={{ fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Browse or search the directory
                      </Typography>
                    </Box>
                    <Box sx={{ px: 3, py: 2.5, backgroundColor: 'background.default' }}>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {TYPES.map((t) => (
                          <TagPill
                            key={t}
                            label={t}
                            active={activeType === t}
                            onClick={() => setActiveType((prev) => (prev === t ? null : t))}
                          />
                        ))}
                        {activeType && (
                          <TagPill
                            label="Clear filter"
                            onClick={() => setActiveType(null)}
                            sx={{ borderColor: 'rgba(184, 115, 51, 0.5)', color: 'warning.main', '&:hover': { backgroundColor: 'rgba(184, 115, 51, 0.12)' } }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Paper>

                  {/* Grouped results */}
                  {groups.map(({ type, orgs }) => (
                    <Box key={type} sx={{ mb: 5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2.5, pb: 1.5, borderBottom: '1px solid rgba(11, 32, 39, 0.08)' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: 'primary.main' }}>
                          {type}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                          {orgs.length} {orgs.length === 1 ? 'organisation' : 'organisations'}
                        </Typography>
                      </Box>
                      <Grid container spacing={3}>
                        {orgs.slice(0, expandedTypes.has(type) ? orgs.length : INITIAL_SHOW).map((org) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={org.id}>
                            <OrganisationCard org={org} />
                          </Grid>
                        ))}
                      </Grid>
                      {orgs.length > INITIAL_SHOW && (
                        <Box sx={{ mt: 2.5 }}>
                          <Button
                            variant="text"
                            size="small"
                            color="primary"
                            onClick={() => toggleExpand(type)}
                            endIcon={expandedTypes.has(type) ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                            sx={{ fontSize: '0.82rem', px: 0 }}
                            disableRipple
                          >
                            {expandedTypes.has(type) ? 'Show fewer' : `Load ${orgs.length - INITIAL_SHOW} more`}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                </>
              )}
            </>
          )}

          {/* Submit CTA */}
          <Paper
            elevation={0}
            sx={{
              mt: 6,
              p: { xs: 3, md: 4 },
              backgroundColor: 'background.paper',
              border: '1px solid rgba(27, 31, 29, 0.1)',
              borderRadius: 3,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 3,
              boxShadow: '0 16px 34px rgba(27, 31, 29, 0.08)',
            }}
          >
            <AddCircleOutlineIcon sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700 }}>
                Help grow the directory
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                The value of this directory depends on collective participation. If your organisation is active in regenerative agriculture in South Africa, or if you know of one that should be included, we encourage you to contribute and help strengthen the ecosystem.
              </Typography>
            </Box>
            <MuiButton
              component={RouterLink}
              to="/submit"
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
              sx={{ flexShrink: 0 }}
            >
              Submit an Organisation
            </MuiButton>
          </Paper>

        </Container>
      </Box>
    </>
  )
}
